/**
 * Pure utility functions for the Projects Index page.
 *
 * Extracted from Index.svelte for testability. Covers:
 * - Multi-select filter toggling
 * - Column sorting with nulls-last semantics
 * - Month key formatting for group-by-month
 * - Project grouping (by month, by status)
 */
import type { Project } from './types';
import { parseCalendarDate } from './utils';
import { getProjectDateRange } from './projectDateRange';

// ---------------------------------------------------------------------------
// Filter helpers
// ---------------------------------------------------------------------------

/** Toggle a value in a Set, returning a new Set (Svelte 5 reactivity safe). */
export function toggleFilterValue(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

export type SortColumn = 'title' | 'status' | 'category' | 'targetDate' | 'tasks';
export type SortDirection = 'asc' | 'desc';

export const STATUS_ORDER: Record<string, number> = {
  planned: 0,
  active: 1,
  done: 2,
};

/**
 * Sort an array of projects by the given column and direction.
 * Null dates and zero-task projects always sort last regardless of direction.
 * Returns a new array (does not mutate the input).
 */
export function sortProjects(
  projects: Project[],
  column: SortColumn,
  direction: SortDirection,
): Project[] {
  const list = [...projects];
  const dir = direction === 'asc' ? 1 : -1;

  list.sort((a, b) => {
    switch (column) {
      case 'title':
        return a.title.toLowerCase().localeCompare(b.title.toLowerCase()) * dir;
      case 'status':
        return ((STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)) * dir;
      case 'category':
        return (
          (a.category || '').toLowerCase().localeCompare((b.category || '').toLowerCase()) * dir
        );
      case 'targetDate': {
        const aDate = a.schedulingState === 'backlog' ? null : getProjectDateRange(a)?.start;
        const bDate = b.schedulingState === 'backlog' ? null : getProjectDateRange(b)?.start;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return aDate.localeCompare(bDate) * dir;
      }
      case 'tasks': {
        const aTotal = a.taskCounts?.total || 0;
        const bTotal = b.taskCounts?.total || 0;
        if (aTotal === 0 && bTotal === 0) return 0;
        if (aTotal === 0) return 1;
        if (bTotal === 0) return -1;
        const aRatio = a.taskCounts!.done / aTotal;
        const bRatio = b.taskCounts!.done / bTotal;
        return (aRatio - bRatio) * dir;
      }
      default:
        return 0;
    }
  });

  return list;
}

// ---------------------------------------------------------------------------
// Group helpers
// ---------------------------------------------------------------------------

export type GroupBy = 'none' | 'month' | 'status';

export interface ProjectGroup {
  key: string;
  label: string;
  projects: Project[];
}

export const STATUS_GROUP_ORDER = ['active', 'planned', 'done'];

export const STATUS_GROUP_LABELS: Record<string, string> = {
  active: '🔄 Active',
  planned: '📋 Planned',
  done: '✅ Done',
};

/**
 * Parse a date string into a { key, label } for month grouping.
 * key: "YYYY-MM" (sortable), label: "Month YYYY" (human-readable).
 */
export function formatMonthKey(dateStr: string): { key: string; label: string } {
  const d = parseCalendarDate(dateStr);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  // Use UTC methods since parseCalendarDate creates UTC-midnight dates
  const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  const label = `${monthNames[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return { key, label };
}

/**
 * Group a sorted list of projects by the given groupBy mode.
 * When groupBy is 'none', returns a single group with key '__all'.
 */
export function groupProjects(projects: Project[], groupBy: GroupBy): ProjectGroup[] {
  if (groupBy === 'none') {
    return [{ key: '__all', label: '', projects }];
  }

  if (groupBy === 'month') {
    const groups = new Map<string, { label: string; projects: Project[] }>();
    for (const p of projects) {
      const dateStr = p.schedulingState === 'backlog' ? null : getProjectDateRange(p)?.start;
      let key: string;
      let label: string;
      if (dateStr) {
        const m = formatMonthKey(dateStr);
        key = m.key;
        label = m.label;
      } else {
        key = '__nodate';
        label = 'No date';
      }
      if (!groups.has(key)) {
        groups.set(key, { label, projects: [] });
      }
      groups.get(key)!.projects.push(p);
    }
    const entries = [...groups.entries()];
    entries.sort(([aKey], [bKey]) => {
      if (aKey === '__nodate') return 1;
      if (bKey === '__nodate') return -1;
      return bKey.localeCompare(aKey); // reverse chronological
    });
    return entries.map(([key, { label, projects: groupedProjects }]) => ({
      key,
      label,
      projects: groupedProjects,
    }));
  }

  if (groupBy === 'status') {
    const groups = new Map<string, Project[]>();
    for (const p of projects) {
      if (!groups.has(p.status)) {
        groups.set(p.status, []);
      }
      groups.get(p.status)!.push(p);
    }
    return STATUS_GROUP_ORDER.filter((s) => groups.has(s)).map((s) => ({
      key: s,
      label: STATUS_GROUP_LABELS[s] || s,
      projects: groups.get(s)!,
    }));
  }

  return [{ key: '__all', label: '', projects }];
}
